import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { apiRequest, ApiRequestError } from '../api/client.js';
import type { ProposalVersion } from '../api/types.js';
import type { ApprovalDecision } from '../../shared/workflow.js';
import { Alert, Button, Card, Field, TextArea } from '../components/ui.js';
import { ConfirmDialog } from '../components/Modal.js';
import { formatCurrency } from '../lib/format.js';

/**
 * Shown only to the USPS authority the proposal requires; the dollar threshold is
 * resolved before this form is rendered and re-checked server-side. The total the
 * approver saw is sent back with the decision, so the server can refuse a decision
 * against a figure that has since changed.
 */
export function ProposalDecisionForm({
  workOrderNumber,
  version,
  onDecided,
  endorse = false,
}: {
  workOrderNumber: string;
  version: ProposalVersion;
  onDecided: () => void;
  /** COR review above their own limit: "approve" endorses and routes to the CO. */
  endorse?: boolean;
}) {
  const [decision, setDecision] = useState<ApprovalDecision | null>(null);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const submit = useMutation({
    mutationFn: (chosen: ApprovalDecision) =>
      apiRequest(`/api/proposal-versions/${version.id}/decision`, {
        method: 'POST',
        body: {
          decision: chosen,
          comment: comment.trim() || undefined,
          acknowledgedTotalCents: version.totalCents,
        },
      }),
    onSuccess: () => {
      setConfirming(false);
      setDecision(null);
      setComment('');
      onDecided();
    },
    onError: (caught: unknown) => {
      setConfirming(false);
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'Your decision could not be recorded. Please try again.',
      );
    },
  });

  const commentRequired = decision !== null && decision !== 'approved';
  const canSubmit = decision !== null && (!commentRequired || comment.trim().length >= 3);

  return (
    <Card title={endorse ? 'Your review is required' : 'Your decision is required'}>
      <div className="flex flex-col gap-4">
        {error && <Alert variant="error">{error}</Alert>}

        <div className="rounded-md bg-brand-50 p-4">
          <p className="text-sm text-slate-700">
            Version {version.versionNo} of the proposal for {workOrderNumber}
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">
            {formatCurrency(version.totalCents)}
          </p>
          <p className="mt-1 text-xs text-slate-600">
            {endorse
              ? 'This amount is above the COR limit. Your endorsement sends the proposal to the Contracting Officer for final approval; VGS is not authorized to proceed until the Contracting Officer approves. Your review is recorded permanently.'
              : 'Approving this proposal authorizes VGS to perform the work at this price. Your approval is recorded permanently and cannot be edited afterward.'}
          </p>
        </div>

        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-semibold text-slate-900">Decision</legend>
          {(
            [
              {
                value: 'approved',
                label: endorse
                  ? 'Endorse — recommend approval and send to the Contracting Officer'
                  : 'Approve — authorize this work at this price',
              },
              {
                value: 'revision_requested',
                label: 'Request a revision — ask VGS to change the proposal',
              },
              { value: 'rejected', label: 'Reject — do not proceed with this work' },
            ] as const
          ).map((option) => (
            <label key={option.value} className="flex items-start gap-2 text-sm text-slate-800">
              <input
                type="radio"
                name="decision"
                className="mt-0.5 h-4 w-4 border-slate-300 text-brand-700 focus:ring-brand-600"
                checked={decision === option.value}
                onChange={() => {
                  setDecision(option.value);
                  setError(null);
                }}
              />
              {option.label}
            </label>
          ))}
        </fieldset>

        <Field
          label="Comment"
          htmlFor="decision-comment"
          required={commentRequired}
          hint={
            commentRequired
              ? 'Explain what needs to change or why the work is not proceeding.'
              : endorse
                ? 'Optional note for the Contracting Officer, recorded with your endorsement.'
                : 'Optional note recorded with your approval.'
          }
        >
          <TextArea
            id="decision-comment"
            rows={4}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
          />
        </Field>

        <div>
          <Button
            disabled={!canSubmit}
            loading={submit.isPending}
            variant={decision === 'rejected' ? 'danger' : 'primary'}
            onClick={() => setConfirming(true)}
          >
            Record decision
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={confirming}
        title={
          decision === 'approved'
            ? endorse
              ? `Endorse ${formatCurrency(version.totalCents)} for Contracting Officer approval?`
              : `Approve ${formatCurrency(version.totalCents)}?`
            : decision === 'rejected'
              ? 'Reject this proposal?'
              : 'Request a revision?'
        }
        message={
          decision === 'approved'
            ? endorse
              ? `The proposal for ${workOrderNumber} goes to the Contracting Officer for final approval. VGS is not notified to proceed until the Contracting Officer approves.`
              : `This authorizes VGS to perform the work on ${workOrderNumber} at ${formatCurrency(version.totalCents)}. The approval record is permanent.`
            : 'Your comment will be sent to VGS and recorded permanently.'
        }
        confirmLabel={decision === 'approved' ? (endorse ? 'Endorse' : 'Approve') : 'Confirm'}
        variant={decision === 'rejected' ? 'danger' : 'primary'}
        onClose={() => setConfirming(false)}
        onConfirm={() => {
          if (decision) submit.mutate(decision);
        }}
      />
    </Card>
  );
}
